import { scaleLinear, scaleLog, scaleTime } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { AnnotationsMarkerConfig } from '../../core/components/annotations/types/annotations-marker-props.js';
import {
  isNumericalAxis,
  XAxisConfig,
} from '../components/axis/types/annotations-x-axis-props.js';

const getValue = (value: number | Date) => {
  return typeof value === 'number' ? value : value.getTime();
};

const getDefaultScale = (xAxisConfig: XAxisConfig, width: number) => {
  const range = [0, width];

  if (isNumericalAxis(xAxisConfig)) {
    let min: number | Date = 0;
    let max: number | Date = 1;
    if (!isUndefined(xAxisConfig.max)) {
      max = xAxisConfig.max;
      if (!isUndefined(xAxisConfig.min)) {
        min = xAxisConfig.min;
      }
    }
    return scaleLinear(range).domain([min, max]);
  }

  return scaleTime(range);
};

/**
 * Logarithmic scale does not support negative or zero values.
 * If present, it should fall back to linear scale
 * @param domain -
 */
const shouldFallbackToLinear = (domain: [number, number]) => domain[0] <= 0;

export const buildScale = (
  markers: AnnotationsMarkerConfig[],
  width: number,
  xAxisConfig: XAxisConfig,
) => {
  if (markers.length <= 0) {
    return getDefaultScale(xAxisConfig, width);
  }

  const { min, max } = xAxisConfig;

  const range = [0, width];

  let domain: [number, number];

  if (isUndefined(min) || isUndefined(max)) {
    domain = calculateDomain(markers, min, max);
  } else {
    domain = [getValue(min), getValue(max)];
  }

  if (isNumericalAxis(xAxisConfig)) {
    const { type } = xAxisConfig;

    if (type === 'linear' || shouldFallbackToLinear(domain)) {
      return scaleLinear(range).domain(domain);
    }

    return scaleLog(range).domain(domain);
  }

  return scaleTime(range).domain(domain);
};

export const calculateDomain = (
  markers: AnnotationsMarkerConfig[],
  min?: number | Date,
  max?: number | Date,
): [number, number] => {
  const timestampsSet = new Set<number>();

  markers.forEach((marker) => {
    timestampsSet.add(getValue(marker.start));

    if (marker.end) {
      timestampsSet.add(getValue(marker.end));
    }
  });

  const timestamps = Array.from(timestampsSet);

  return [
    min ? getValue(min) : Math.min(...timestamps),
    max ? getValue(max) : Math.max(...timestamps),
  ];
};
